import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEventType } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { Secure } from '../../enums/secure.enum';
import { Options } from './_models/base-data-options.model';
// import { plainToClassFromExist } from 'class-transformer';
import { UrlUtil } from '../../util/url/url.util';
import { BaseModel } from '../../models/base/base.model';
import { ErrorDialogService } from '../../dialogs/error/error-dialog.service';
import { Dto } from '../../models/dto/dto.model';
import { ItemDto } from '../../models/dto/item-dto.model';
import { ClientErrorReport } from '../../models/error/client-error-report.model';
import { map } from 'rxjs/operators';
import { LeathermanAppConfigInjectionToken } from '../../config/injection-tokens/leatherman-app-config.injection-token';
import { ILeathermanAppConfig } from '../../config/app.config';
import { ErrorDialogServiceInjectionToken } from '../../config/injection-tokens/error-dialog-service.injection-token';
import { SearchResultsDto } from '../../models/dto/search-results-dto.model';
import { SearchResultsContainer } from '../../models/dto/search-results-container.model';
import { UploadEvent } from '../../models/events/upload-event.model';
import { ArrayDto } from '../../models/dto/array-dto.model';

/**
 * The base data service for communicating with API endpoints
 */
@Injectable({
  providedIn: 'root'
})
export class BaseDataService {
  // **********************
  // * Protected properties
  // **********************

  /** Set accessor for the base endpoint */
  protected set baseEndpoint(baseEndpoint: string) {
    this._baseEndpoint = baseEndpoint;
    if (this._baseEndpoint.startsWith('/') === false) {
      this._baseEndpoint = '/' + this._baseEndpoint;
    }
    if (this._baseEndpoint.endsWith('/') === false) {
      this._baseEndpoint = this._baseEndpoint + '/';
    }
  }

  /** Set accessor for the base endpoint */
  protected get baseEndpoint(): string {
    return this._baseEndpoint;
  }

  /** The base endpoint */
  private _baseEndpoint: string;

  /** Set accessor for the data service's item data type */
  protected set type(type: any) {
    this._type = type;
  }

  /** The data service's item data type */
  private _type: any;

  /** Set accessor for the api root */
  protected get apiRoot(): string {
    if (this.config.apiRoot) {
      return this.config.serverUrl + '/' + this.config.apiRoot;
    }
    return this.config.serverUrl;
  }

  /** Get accessor for the API key  */
  protected get apiKey(): string {
    return this.config.apiKey;
  }

  /** Get accessor for the no token header */
  protected get noTokenHeader(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: 'Sat, 01 Jan 2000 00:00:00 GMT'
    });
  }

  /** Get accessor to the header to use when the API endpoint is secured with an API key */
  protected get apiHeader(): HttpHeaders {
    const token = this.apiKey ? 'Bearer ' + this.apiKey : '';
    const apiHeader = new HttpHeaders({
      Authorization: token,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: 'Sat, 01 Jan 2000 00:00:00 GMT'
    });
    return apiHeader;
  }

  /** Get acccessor for the header to use when the API endpoit is secured with a JWT token */
  protected get authHeader(): HttpHeaders {
    const localStorageToken = localStorage.getItem(this.config.jwtTokenName);
    const token = localStorageToken ? 'Bearer ' + localStorageToken : '';
    const authHeader = new HttpHeaders({
      Authorization: token,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: 'Sat, 01 Jan 2000 00:00:00 GMT'
    });
    return authHeader;
  }

  /**
   * Constructor
   * @param config - The app config object
   * @param errorDialogService - A reference to the error dialog service
   * @param router - A reference to the router
   * @param httpClient - A reference to the HTTP client service
   */
  constructor(
    @Inject(LeathermanAppConfigInjectionToken)
    protected config: ILeathermanAppConfig,
    @Inject(ErrorDialogServiceInjectionToken)
    protected errorDialogService: ErrorDialogService,
    protected router: Router,
    protected httpClient: HttpClient
  ) { }

  // **********************
  // * Protected methods
  // **********************

  /**
   * Call a standard delete API endpoint
   * @param id - The ID of the item to be deleted
   * @param secure - Enum indicating whether the API endpoint is secure
   * @returns True if the delete succeeded
   * @async
   */
  protected async _delete(id: string, secure: Secure): Promise<boolean> {
    if (
      (await this.validateId(id, 'Error deleting a record. Invalid ID.')) ===
      false
    ) {
      return false;
    }
    const headers = this.getHeader(secure);
    const result = await this.httpClient
      .delete<Dto>(UrlUtil.join(this.apiRoot, this._baseEndpoint, id), {
        headers
      })
      .toPromise();

    if (Dto.isError(result.status.code)) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return false;
    }

    return true;
  }

  /**
   * Call a non-standard delete API endpoint
   * @param options - The request options
   * @returns True if the delete succeeded
   * @async
   */
  protected async _deleteWithOptions(options: Options): Promise<boolean> {
    let url: string;
    if (options.url) {
      url = options.url;
    } else {
      url = UrlUtil.join(this.apiRoot, this._baseEndpoint, options.url);
    }
    if (options.id) {
      url = UrlUtil.join(url, options.id);
    }
    if (options.parameters.hasParameters) {
      url = UrlUtil.join(url, options.parameters.getParamterString());
    }
    const headers = this.getHeader(options.secure);
    const result = await this.httpClient
      .delete<Dto>(url, {
        headers
      })
      .toPromise();

    if (Dto.isError(result.status.code, options.suppressedErrors)) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return false;
    }

    return true;
  }

  /**
   * Delete all test artifacts
   * @returns True if the delete succeded
   * @async
   */
  public async _deleteTestArtifacts(): Promise<boolean> {
    const options = new Options();
    options.url = UrlUtil.join(
      this.apiRoot,
      this.baseEndpoint,
      'deletetestartifacts'
    );
    options.secure = Secure.true;
    return await this._deleteWithOptions(options).catch(async err => {
      return await this.handlePromiseError(
        'BaseDataService',
        '_deleteTestArtifacts',
        err
      );
    });
  }

  /**
   * Call a standard get API endpoint
   * @param id - The ID of the item to get
   * @param secure - Enum indicating whether the API endpoint is secure
   * @returns The item if the get succeeded
   * @async
   */
  protected async _get<T>(id: string, secure: Secure): Promise<T> {
    // Validate the passed ID
    this.validateId(id, 'Error getting a record. Invalid ID.');
    // Get the appropriate header object
    const headers = this.getHeader(secure);
    // Make an async call using the httpClient. Expect the result to be an ItemDto object
    const result = await this.httpClient
      .get<ItemDto<T>>(UrlUtil.join(this.apiRoot, this._baseEndpoint, id), {
        headers
      })
      // Convert the result to a promise
      .toPromise();

    // If the returned status code is not a success code then
    if (Dto.isError(result.status.code)) {
      // Open the error dialog
      this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      // Return null
      return null;
    }

    // If the returned data is not an object, return the object with casting it
    if (typeof result.data !== 'object') {
      return result.data;
    }

    // // Cast the object to the appropriate class
    // const data = plainToClassFromExist(this._type, result.data);

    // Return the object
    return result.data;
  }

  // _getObservable
  protected _getObservable<T>(id: string, secure: Secure): Observable<T> {
    // Validate the passed ID
    this.validateId(id, 'Error getting a record. Invalid ID.');
    // Get the appropriate header object
    const headers = this.getHeader(secure);
    // Make an async call using the httpClient. Expect the result to be an ItemDto object
    return this.httpClient
      .get<ItemDto<T>>(UrlUtil.join(this.apiRoot, this._baseEndpoint, id), {
        headers
      })
      .pipe(
        map(result => {
          return result.data;
        })
      );
  }

  /**
   * Call a non-standard get API endpoint that returns an observable
   * @param id - The ID of the item to get
   * @param options - The request options
   * @returns The item if the get succeeded (as an observable)
   * @async
   */
  protected _getObservableWithOptions<T>(options: Options): Observable<T> {
    let url: string;
    if (options.url) {
      url = options.url;
    } else {
      url = UrlUtil.join(this.apiRoot, this._baseEndpoint, options.url);
    }
    if (options.id) {
      url = UrlUtil.join(url, options.id);
    }
    if (options.parameters.hasParameters) {
      url = UrlUtil.join(url, options.parameters.getParamterString());
    }
    const headers = this.getHeader(options.secure);
    return this.httpClient
      .get<ItemDto<T>>(url, {
        headers
      })
      .pipe(
        map(result => {
          return result.data;
        })
      );
  }

  /**
   * Call a non-standard get API endpoint
   * @param options - The request options
   * @returns The item if the get succeeded
   * @async
   */
  protected async _getWithOptions<T>(options: Options): Promise<T> {
    let url: string;
    if (options.url) {
      url = options.url;
    } else {
      url = UrlUtil.join(this.apiRoot, this._baseEndpoint, options.url);
    }
    if (options.id) {
      url = UrlUtil.join(url, options.id);
    }
    if (options.parameters.hasParameters) {
      url = UrlUtil.join(url, options.parameters.getParamterString());
      console.log('Cache busting URL:' + url);
    }
    const headers = this.getHeader(options.secure);
    const result = await this.httpClient
      .get<ItemDto<T>>(url, {
        headers
      })
      .toPromise();

    if (Dto.isError(result.status.code, options.suppressedErrors)) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return null;
    }

    if (result.data === null || result.data === undefined) {
      return null;
    }

    if (typeof result.data !== 'object') {
      return result.data;
    }

    // let data: T;
    // if (options.objectType) {
    //   data = plainToClassFromExist(options.objectType, result.data);
    // } else {
    //   data = plainToClassFromExist(this._type, result.data);
    // }

    return result.data;
  }

  /**
   * Call a standard get many API endpoint
   * @param secure - Enum indicating whether the API endpoint is secure
   * @returns An array of items if the get succeeded
   * @async
   */
  protected async _getMany<T>(secure: Secure): Promise<T[]> {
    const headers = this.getHeader(secure);
    const result = await this.httpClient
      .get<ArrayDto<T>>(UrlUtil.join(this.apiRoot, this._baseEndpoint), {
        headers
      })
      .toPromise();

    if (Dto.isError(result.status.code)) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return null;
    }

    return result.data;
  }

  /**
   * Call a non-standard get many API endpoint
   * @param options - The request options
   * @returns An array of items if the get succeeded
   * @async
   */
  protected async _getManyWithOptions<T>(options: Options): Promise<T[]> {
    let url: string;
    if (options.url) {
      url = options.url;
    } else {
      url = UrlUtil.join(this.apiRoot, this._baseEndpoint, options.url);
    }
    if (options.id) {
      url = UrlUtil.join(url, options.id);
    }
    if (options.parameters.hasParameters) {
      url = UrlUtil.join(url, options.parameters.getParamterString());
    }
    const headers = this.getHeader(options.secure);
    const result = await this.httpClient
      .get<ArrayDto<T>>(url, { headers })
      .toPromise();

    if (Dto.isError(result.status.code, options.suppressedErrors)) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return null;
    }

    return result.data;
  }

  /**
   * Call a standard post API endpoint
   * @param object - The object to post
   * @param secure - Enum indicating whether the API endpoint is secure
   * @param validateId - Boolean indicating whether then object's ID should be validated
   * @returns The posted object
   * @async
   */
  protected async _post<T>(
    object: any,
    secure: Secure,
    validateId: boolean = true
  ): Promise<T> {
    if (validateId) {
      if (
        !this.validateModelId(
          object,
          'Error posting a record. Invalid model ID.'
        )
      ) {
        return null;
      }
    }
    const headers = this.getHeader(secure);
    const url = UrlUtil.join(this.apiRoot, this._baseEndpoint);

    let result: ItemDto<T>;
    try {
      result = await this.httpClient
        .post<ItemDto<T>>(
          UrlUtil.join(this.apiRoot, this._baseEndpoint),
          object,
          {
            headers
          }
        )
        .toPromise();
    } catch (error) {
      console.log('**********');
      console.log('HTTP Post Error');
      console.log('URL: ' + url);
      console.log(object);
      throw error;
    }

    if (Dto.isError(result.status.code)) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return null;
    }

    return result.data;
  }

  /**
   * Call a non-standard post API endpoint
   * @param object - The object to post
   * @param options - The request options
   * @returns The posted object
   * @async
   */
  protected async _postWithOptions<T>(
    object: any,
    options: Options
  ): Promise<T> {
    if (options.validateId) {
      if (
        !this.validateModelId(
          object,
          'Error posting a record. Invalid model ID.'
        )
      ) {
        return null;
      }
    }
    let url: string;
    if (options.url) {
      url = options.url;
    } else {
      url = UrlUtil.join(this.apiRoot, this._baseEndpoint, options.url);
    }
    if (options.id) {
      url += options.id;
    }
    if (options.parameters.hasParameters) {
      url += options.parameters.getParamterString();
    }
    const headers = this.getHeader(options.secure);

    const result = await this.httpClient
      .post<ItemDto<T>>(url, object, {
        headers
      })
      .toPromise();

    if (Dto.isError(result.status.code, options.suppressedErrors)) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return null;
    }

    return result.data;
  }

  /**
   * Call a standard put API endpoint
   * @param object - The object to put
   * @param secure - Enum indicating whether the API endpoint is secure
   * @param validateId - Boolean indicating whether then object's ID should be validated
   * @returns The updated object
   * @async
   */
  protected async _put<T extends BaseModel>(
    object: any,
    secure: Secure,
    validateId: boolean = true
  ): Promise<T> {
    if (object && validateId) {
      if (
        !this.validateModelId(
          object,
          'Error putting a record. Invalid model ID.'
        )
      ) {
        return null;
      }
    }
    const headers = this.getHeader(secure);
    const result = await this.httpClient
      .put<ItemDto<T>>(
        UrlUtil.join(this.apiRoot, this._baseEndpoint, object._id),
        object,
        {
          headers
        }
      )
      .toPromise();

    if (Dto.isError(result.status.code)) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return null;
    }

    return result.data;
  }

  /**
   * Call a non-standard put API endpoint
   * @param object - The object to put
   * @param options - The request options
   * @returns The updated object
   * @async
   */
  protected async _putWithOptions<T>(
    options: Options,
    object?: any
  ): Promise<T> {
    if (object && options.validateId) {
      if (
        !this.validateModelId(
          object,
          'Error putting a record. Invalid model ID.'
        )
      ) {
        return null;
      }
    }
    let url: string;
    if (options.url) {
      url = options.url;
    } else {
      url = UrlUtil.join(this.apiRoot, this._baseEndpoint, options.url);
    }
    if (options.id) {
      url = UrlUtil.join(url, options.id);
    }
    if (options.parameters.hasParameters) {
      url = UrlUtil.join(url, options.parameters.getParamterString());
    }
    const headers = this.getHeader(options.secure);
    const result = await this.httpClient
      .put<ItemDto<T>>(url, object, {
        headers
      })
      .toPromise();

    if (Dto.isError(result.status.code, options.suppressedErrors)) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return null;
    }

    return result.data;
  }

  protected async _searchWithOptions<T>(
    options: Options
  ): Promise<SearchResultsContainer<T>> {
    let url: string;
    if (options.url) {
      url = options.url;
    } else {
      url = UrlUtil.join(this.apiRoot, this.baseEndpoint, options.url);
    }
    if (options.id) {
      url = UrlUtil.join(url, options.id);
    }
    if (options.parameters.hasParameters) {
      url = UrlUtil.join(url, options.parameters.getParamterString());
      console.log('Cache busting URL:' + url);
    }
    const headers = this.getHeader(options.secure);
    const result = await this.httpClient
      .get<SearchResultsDto<T>>(url, {
        headers
      })
      .toPromise();

    if (
      SearchResultsDto.isError(result.status.code, options.suppressedErrors)
    ) {
      await this.errorDialogService.openErrorDialog(
        result.status.text,
        result.status.message
      );
      return null;
    }

    const searchResultsContainer = new SearchResultsContainer<T>(result);
    return searchResultsContainer;
  }

  protected _uploadFormData(
    url: string,
    data: FormData,
    secure: Secure
  ): Observable<UploadEvent> {
    const headers = this.getHeader(secure);
    return this.httpClient
      .post<any>(url, data, {
        headers,
        reportProgress: true,
        observe: 'events'
      })
      .pipe(
        map(event => {
          let uploadEvent: UploadEvent;
          switch (event.type) {
            case HttpEventType.UploadProgress:
              const progress = Math.round((100 * event.loaded) / event.total);
              uploadEvent = new UploadEvent('Progress');
              uploadEvent.progressPercent = progress;
              return uploadEvent;

            case HttpEventType.Response:
              uploadEvent = new UploadEvent('Response', event.body);
              return uploadEvent;
            default:
              uploadEvent = new UploadEvent(
                'Error',
                `Unhandled event: ${event.type}`
              );
              return uploadEvent;
          }
        })
      );
  }

  /**
   * Handle an error for an observable request
   * @param serviceName - The name of the service reporting the error
   * @param result - The return to return
   */
  protected handleObservableError(serviceName: string, result = {}) {
    return (error: ClientErrorReport): Observable<any> => {
      console.error(serviceName, error);
      console.error(serviceName + ' error status', error.status);
      return of(result);
    };
  }

  /**
   * Handle an error for a standard promise request
   * @param serviceName - The name of the service reporting the error
   * @param method - The name of the method reporting the error
   * @param error - The error object
   * @async
   */
  protected async handlePromiseError(
    serviceName: string,
    method: string,
    error: any
  ): Promise<null> {
    console.log(
      'Service: ' +
      serviceName +
      ' Method: ' +
      method +
      ' Message: ' +
      error.message
    );

    await this.errorDialogService.openErrorDialog(
      'Unhandled Exception',
      'An unhandled exception error occurred: ' + error.message
    );
    return null;
  }

  /**
   * Get the request header
   * @param secure - Enum indicating the type of API endpoing security
   * @returns An HTTP header
   */
  protected getHeader(secure: Secure): HttpHeaders {
    switch (secure) {
      case Secure.true:
        return this.authHeader;
      case Secure.api:
        return this.apiHeader;
      case Secure.false:
      default:
        return this.noTokenHeader;
    }
  }

  /**
   * Validate the ID of a model
   * @param model - The model to validate
   * @param message - The message to display if validating fails
   * @returns True if the validation succeeded
   * @async
   */
  private async validateModelId(model: any, message: string): Promise<boolean> {
    if (!model._id) {
      await this.errorDialogService.openErrorDialog(
        'Invalid Model ID',
        message
      );
      return false;
    }
    return true;
  }

  /**
   * Validate the ID of a model
   * @param id - The ID to validate
   * @param message - The message to display if validating fails
   * @returns True if the validation succeeded
   * @async
   */
  private async validateId(id: string, message: string): Promise<boolean> {
    if (!id) {
      await this.errorDialogService.openErrorDialog(
        'Invalid Model ID',
        message
      );
      return false;
    }
    return true;
  }
}
